import { DailyEventObject } from '@daily-co/daily-js';
import { useDaily, useDailyEvent } from '@daily-co/daily-react';
import { datadogLogs } from '@datadog/browser-logs';
import { callLogContext } from '@web-app/utils/daily-client/call-log-context';
import { useCallback, useEffect, useRef, useState } from 'react';

const MAX_TRANSCRIPTION_RETRIES = 5;

interface UseAutoTranscriptionArgs {
  roomId: string;
  participantCount: number;
  isProvider: boolean;
  enabled: boolean;
}

/*
 * Auto-starts Daily.co real-time transcription when both provider and patient
 * are in a call. Gated behind the `real-time-transcript` GrowthBook flag.
 *
 * Transcription lifecycle:
 *   - Starts when participantCount >= 2 and flag is enabled
 *   - Stops when provider ends the call (stopTranscription in useEndAppointment)
 *   - Retries up to MAX_TRANSCRIPTION_RETRIES on error, then gives up
 *   - Resets retry count when transcription messages are received or participant leaves
 *
 * Domain-level configuration required (one-time setup per environment):
 *
 * We leverage Daily's custom S3 bucket transcript storage, which writes the
 * accumulated VTT every 2 minutes during active transcription. This allows
 * the server to read the growing transcript from S3 for downstream processing
 * (classification, signal extraction) without client-side event forwarding.
 * If we decide to process transcription-message events directly in the future,
 * transcription storage can be turned off.
 *
 *   1. Enable transcription storage so VTT files persist post-session:
 *      POST https://api.daily.co/v1/
 *      { "properties": { "enable_transcription_storage": true } }
 *
 *   2. Configure custom S3 bucket for transcription output:
 *      POST https://api.daily.co/v1/
 *      { "properties": { "transcription_bucket": {
 *          "bucket_name": "appointment-transcription",
 *          "bucket_region": "us-east-1",
 *          "assume_role_arn": "arn:aws:iam::{account}:role/{role}",
 *          "allow_api_access": true
 *      }}}
 *      Daily writes VTT to S3 every 2 minutes during active transcription.
 *      Bucket provisioned via daily-recordings-bucket CDK project.
 *
 *   3. Customize S3 key template (removes subdomain prefix):
 *      POST https://api.daily.co/v1/
 *      { "properties": { "transcription_template": "{room_name}/{epoch_time}" } }
 *
 * Docs:
 *   - Transcription guide: https://docs.daily.co/guides/products/transcription
 *   - Custom S3 bucket setup: https://docs.daily.co/guides/products/live-streaming-recording/storing-recordings-in-a-custom-s3-bucket
 *   - Domain config (transcription_template): https://docs.daily.co/reference/rest-api/your-domain/config#transcription_template
 */
export function useAutoTranscription({
  roomId,
  participantCount,
  isProvider,
  enabled,
}: UseAutoTranscriptionArgs) {
  const callObject = useDaily();
  const [isTranscriptionActive, setIsTranscriptionActive] = useState(false);
  const retryCountRef = useRef(0);

  useDailyEvent(
    'transcription-started',
    useCallback(
      (event: DailyEventObject<'transcription-started'>) => {
        setIsTranscriptionActive(true);

        datadogLogs.logger.info(
          'Transcription started',
          callLogContext('transcription', {
            roomId,
            participantType: 'provider',
            transcriptId: event.transcriptId,
            language: event.language,
            model: event.model,
            startedBy: event.startedBy,
          }),
        );
      },
      [roomId],
    ),
  );

  useDailyEvent(
    'transcription-stopped',
    useCallback(
      (event: DailyEventObject<'transcription-stopped'>) => {
        setIsTranscriptionActive(false);

        datadogLogs.logger.info(
          'Transcription stopped',
          callLogContext('transcription', {
            roomId,
            participantType: 'provider',
            updatedBy: event.updatedBy,
          }),
        );
      },
      [roomId],
    ),
  );

  useDailyEvent(
    'transcription-error',
    useCallback(
      (event: DailyEventObject<'transcription-error'>) => {
        retryCountRef.current += 1;
        const attempt = retryCountRef.current;

        if (attempt >= MAX_TRANSCRIPTION_RETRIES) {
          setIsTranscriptionActive(false);

          datadogLogs.logger.error(
            'Transcription failed after max retries',
            callLogContext('transcription', {
              roomId,
              participantType: 'provider',
              errorMsg: event.errorMsg,
              attempt,
              maxRetries: MAX_TRANSCRIPTION_RETRIES,
            }),
          );
          return;
        }

        setIsTranscriptionActive(false);

        datadogLogs.logger.warn(
          'Transcription error, will retry',
          callLogContext('transcription', {
            roomId,
            participantType: 'provider',
            errorMsg: event.errorMsg,
            attempt,
            maxRetries: MAX_TRANSCRIPTION_RETRIES,
          }),
        );
      },
      [roomId],
    ),
  );

  useDailyEvent(
    'transcription-message',
    useCallback((_event: DailyEventObject<'transcription-message'>) => {
      // NOTE: we can swap to pushing is_final events to backend if we want to store it ourselves using s3 persistance daily provides
      // Receiving messages means transcription is working — reset retry count
      // Setting this in transcription-started event causes it to never stop retrying in failure scenario.
      if (retryCountRef.current > 0) retryCountRef.current = 0;
    }, []),
  );

  // Reset retry count when participant leaves — allows fresh retries on rejoin
  useEffect(
    function resetRetriesOnParticipantLeave() {
      if (participantCount < 2) {
        retryCountRef.current = 0;
      }
    },
    [participantCount],
  );

  useEffect(
    function startTranscriptionWhenReady() {
      if (
        !enabled ||
        !isProvider ||
        participantCount < 2 ||
        isTranscriptionActive ||
        retryCountRef.current >= MAX_TRANSCRIPTION_RETRIES ||
        !callObject
      ) {
        return;
      }

      /*
       * Deepgram streaming config via Daily's startTranscription.
       * See: https://developers.deepgram.com/docs/stt-streaming-feature-overview
       *
       * endpointing (1500ms): silence threshold before finalizing an audio segment (is_final).
       *   Higher = more audio context for interim revision, improving accuracy.
       *   Deepgram recommends 300-500ms for low-latency; we use 1500ms since we classify
       *   from S3 every 2 minutes and latency is not a concern.
       *
       * includeRawResponse: exposes Deepgram's is_final and speech_final flags
       *   on transcription-message events, allowing us to filter out interim results.
       *
       * extra: passed directly to Deepgram's streaming API as query params.
       *   - smart_format: auto-formats numbers, dates, currency (e.g., "one fifty" → "150").
       *     Subsumes punctuate — no need for both.
       *   - interim_results: Deepgram emits speculative transcripts that get revised as
       *     more audio arrives. We filter to is_final only in the listener, but enabling
       *     this gives Deepgram a revision window within each utterance for better accuracy.
       *   - utterance_end_ms (1000ms): independent signal when speaker is done talking.
       *     Requires interim_results: true. Works alongside endpointing.
       *   - numerals: converts spoken numbers to digits (e.g., "one fifty" → "150").
       *
       * Accumulation pattern (implemented in Step 2):
       *   Buffer is_final chunks → flush on speech_final → produces complete utterances.
       */
      callObject.startTranscription({
        language: 'en',
        model: 'nova-3-medical',
        profanity_filter: false,
        punctuate: true,
        endpointing: 1500,
        includeRawResponse: true,
        extra: {
          smart_format: true,
          interim_results: true,
          utterance_end_ms: 1000,
          numerals: true,
        },
      });

      datadogLogs.logger.info(
        'Auto-start transcription requested',
        callLogContext('transcription', {
          roomId,
          participantType: 'provider',
          participantCount,
        }),
      );
    },
    [
      enabled,
      isProvider,
      participantCount,
      callObject,
      roomId,
      isTranscriptionActive,
    ],
  );
}
